import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, Users, Zap, Target, Star, ArrowRight, Check, ChevronDown } from 'lucide-react';

const SPORTS_GRID = [
  { emoji: '🏀', name: 'Basketball' }, { emoji: '⚽', name: 'Soccer' },
  { emoji: '🎾', name: 'Tennis' }, { emoji: '🏐', name: 'Volleyball' },
  { emoji: '🏸', name: 'Pickleball' }, { emoji: '🏏', name: 'Cricket' },
  { emoji: '🏓', name: 'Ping Pong' }, { emoji: '🏒', name: 'Hockey' },
  { emoji: '⛳', name: 'Golf' }, { emoji: '🎰', name: 'Poker' },
  { emoji: '⚾', name: 'Baseball' }, { emoji: '🏊', name: 'Swimming' },
];

const ACTIVITY_FEED = [
  { avatar: '🧑‍🦱', name: 'Jordan M.', action: 'just joined a Basketball tournament', time: '2m ago', sport: '🏀' },
  { avatar: '👩‍🦰', name: 'Sarah K.', action: 'created squad "Ace Smashers"', time: '5m ago', sport: '🎾' },
  { avatar: '🧑', name: 'Mike T.', action: 'scored 3 goals in Soccer match', time: '8m ago', sport: '⚽' },
  { avatar: '👨‍🦲', name: 'Chris L.', action: 'won the Friday Night Hoops championship', time: '12m ago', sport: '🏀' },
  { avatar: '👩', name: 'Priya S.', action: 'invited teammates to join Volleyball squad', time: '15m ago', sport: '🏐' },
];

const PLANS = [
  {
    name: 'Player',
    price: 'Free',
    color: '#22c55e',
    features: ['Join unlimited tournaments', 'Create 1 squad', 'Live scoring access', 'Player profile & stats', 'Community social feed'],
    cta: 'Start Free',
    highlight: false,
  },
  {
    name: 'Organizer',
    price: 'Free',
    badge: 'Most Popular',
    color: '#f97316',
    features: ['Everything in Player', 'Create unlimited tournaments', 'Unlimited squads', 'Facility management', 'Advanced analytics', 'Marketplace listings'],
    cta: 'Get Started',
    highlight: true,
  },
];

export default function LandingV2Page() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [activeActivity, setActiveActivity] = useState(0);
  const [activeSportIdx, setActiveSportIdx] = useState(null);

  useEffect(() => {
    const t = setInterval(() => setActiveActivity(a => (a + 1) % ACTIVITY_FEED.length), 2500);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{ backgroundColor: '#0a0a0a', color: '#fff', minHeight: '100vh', fontFamily: 'Inter, system-ui, sans-serif', overflowX: 'hidden' }}>
      <style>{`
        @keyframes slideUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
        @keyframes rotateIn { from{opacity:0;transform:rotate(-8deg) scale(0.9)} to{opacity:1;transform:rotate(0) scale(1)} }
        @keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        .shimmer-text {
          background: linear-gradient(90deg, #fff 0%, #f97316 25%, #fff 50%, #3b82f6 75%, #fff 100%);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 4s linear infinite;
        }
        .orange-gradient { background: linear-gradient(135deg, #f97316, #ef4444); }
        .blue-gradient { background: linear-gradient(135deg, #3b82f6, #6366f1); }
        .green-gradient { background: linear-gradient(135deg, #22c55e, #10b981); }
        .card-3d { transition: transform 0.4s ease, box-shadow 0.4s ease; perspective: 1000px; }
        .card-3d:hover { transform: rotateY(-4deg) rotateX(4deg) translateY(-6px); box-shadow: 20px 20px 60px rgba(0,0,0,0.5); }
        .sport-pill { transition: all 0.2s ease; cursor: pointer; }
        .sport-pill:hover { transform: scale(1.1) translateY(-2px); }
        .activity-item { animation: slideUp 0.4s ease forwards; }
        .neon-border { border: 1px solid transparent; background: linear-gradient(#0a0a0a,#0a0a0a) padding-box, linear-gradient(135deg, #f97316, #3b82f6) border-box; }
      `}</style>

      {/* === TOP BANNER === */}
      <div className="text-center py-3 text-sm font-bold" style={{ background: 'linear-gradient(90deg, #f97316, #ef4444)', color: '#fff' }}>
        🎉 &nbsp;New: Live Tournament Brackets Now Available — <button onClick={() => navigate('/tournaments')} className="underline">View Tournaments →</button>
      </div>

      {/* === HERO === */}
      <section className="relative overflow-hidden" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
        {/* Background gradient orbs */}
        <div style={{ position: 'absolute', top: '-20%', left: '-10%', width: '60%', height: '60%', background: 'radial-gradient(circle, rgba(249,115,22,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-10%', right: '-10%', width: '50%', height: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />

        <div className="max-w-7xl mx-auto px-6 py-20 w-full grid md:grid-cols-2 gap-12 items-center">
          {/* Left: Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8 text-sm font-bold"
              style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', color: '#fb923c' }}>
              <span style={{ animation: 'blink 1.5s infinite' }}>●</span>
              3 Live Matches Right Now
            </div>

            <h1 className="font-black leading-none mb-6" style={{ fontSize: 'clamp(3rem,7vw,5.5rem)' }}>
              <span style={{ color: '#fff' }}>The Sports</span>
              <br />
              <span className="shimmer-text">Platform</span>
              <br />
              <span style={{ color: '#fff' }}>Built for</span>
              <br />
              <span style={{ color: '#f97316' }}>Athletes.</span>
            </h1>

            <p style={{ color: '#9ca3af', fontSize: '1.125rem', lineHeight: 1.7, marginBottom: '2.5rem', maxWidth: '480px' }}>
              Run tournaments. Build squads. Track live scores. Connect with athletes near you.
              Kynaix Sports is how serious players manage their sports life.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <button
                onClick={() => navigate(isAuthenticated ? '/tournaments' : '/signup')}
                className="flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-black text-lg transition-all"
                style={{ background: 'linear-gradient(135deg, #f97316, #ef4444)', color: '#fff', boxShadow: '0 8px 40px rgba(249,115,22,0.4)' }}>
                Start Playing Free <ArrowRight size={20} />
              </button>
              <button
                onClick={() => navigate('/tournaments')}
                className="flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-bold text-lg"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff' }}>
                Browse Tournaments
              </button>
            </div>

            {/* Social proof */}
            <div className="flex items-center gap-4">
              <div className="flex -gap-x-2">
                {['🧑', '👩', '🧑‍🦱', '👨‍🦰', '🧑‍🦲'].map((a, i) => (
                  <div key={i} className="w-9 h-9 rounded-full flex items-center justify-center text-base border-2"
                    style={{ borderColor: '#0a0a0a', background: `hsl(${i * 60}, 70%, 30%)` }}>{a}</div>
                ))}
              </div>
              <div>
                <div className="flex gap-0.5 mb-1">{[1,2,3,4,5].map(i => <Star key={i} size={14} fill="#f97316" stroke="none" />)}</div>
                <p style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Loved by <strong style={{ color: '#fff' }}>10,000+</strong> athletes</p>
              </div>
            </div>
          </div>

          {/* Right: Live Activity + Sports Grid */}
          <div className="flex flex-col gap-6">
            {/* Live Activity Card */}
            <div className="rounded-2xl p-5 neon-border">
              <div className="flex items-center gap-2 mb-4">
                <span className="w-2 h-2 rounded-full bg-green-400" style={{ boxShadow: '0 0 8px #4ade80', animation: 'blink 1s infinite' }} />
                <span className="text-sm font-bold text-green-400 uppercase tracking-wider">Live Activity</span>
              </div>
              <div className="space-y-3">
                {ACTIVITY_FEED.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl transition-all"
                    style={{
                      background: i === activeActivity ? 'rgba(249,115,22,0.08)' : 'transparent',
                      border: `1px solid ${i === activeActivity ? 'rgba(249,115,22,0.2)' : 'transparent'}`,
                    }}>
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-lg flex-shrink-0"
                      style={{ background: 'rgba(255,255,255,0.05)' }}>{item.avatar}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm" style={{ color: '#e5e7eb' }}>
                        <strong style={{ color: '#fff' }}>{item.name}</strong> {item.action}
                      </p>
                      <p className="text-xs" style={{ color: '#6b7280' }}>{item.time}</p>
                    </div>
                    <span className="text-xl flex-shrink-0">{item.sport}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sports Grid */}
            <div className="grid grid-cols-4 gap-2">
              {SPORTS_GRID.map((s, i) => (
                <div
                  key={s.name}
                  className="sport-pill flex flex-col items-center gap-1 py-3 px-2 rounded-xl text-center"
                  onMouseEnter={() => setActiveSportIdx(i)}
                  onMouseLeave={() => setActiveSportIdx(null)}
                  style={{
                    background: activeSportIdx === i ? 'rgba(249,115,22,0.12)' : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${activeSportIdx === i ? 'rgba(249,115,22,0.3)' : 'rgba(255,255,255,0.06)'}`,
                  }}>
                  <span style={{ fontSize: '1.5rem' }}>{s.emoji}</span>
                  <span style={{ fontSize: '0.65rem', color: '#9ca3af', fontWeight: 600 }}>{s.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === STATS STRIP === */}
      <div style={{ background: 'rgba(249,115,22,0.08)', borderTop: '1px solid rgba(249,115,22,0.15)', borderBottom: '1px solid rgba(249,115,22,0.15)' }}>
        <div className="max-w-5xl mx-auto px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: '10,000+', l: 'Active Players' }, { n: '500+', l: 'Tournaments Run' },
            { n: '50+', l: 'Cities' }, { n: '250+', l: 'Facilities' }
          ].map(s => (
            <div key={s.l}>
              <div className="text-3xl font-black" style={{ color: '#f97316' }}>{s.n}</div>
              <div className="text-sm font-medium" style={{ color: '#6b7280' }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* === FEATURES: ALTERNATING === */}
      <section className="py-20 px-6 max-w-6xl mx-auto space-y-24">
        {[
          {
            tag: 'Tournaments',
            icon: '🏆',
            title: 'Run Pro-Level Tournaments in Minutes',
            desc: 'Create bracket tournaments, round robins, and leagues. Manage registrations, draw brackets automatically, run live scoring, and publish results — all from one dashboard.',
            bullets: ['Bracket & round-robin formats', 'Live scoring for 10+ sports', 'Automatic standings & rankings', 'Registration management'],
            color: '#f97316',
            gradient: 'linear-gradient(135deg, rgba(249,115,22,0.12), rgba(239,68,68,0.08))',
            emoji: '🏀⚽🎾🏐',
            flip: false,
          },
          {
            tag: 'Squads',
            icon: '👥',
            title: 'Your Team. Your Way.',
            desc: 'Squads are your sports home base. Chat in real-time, coordinate schedules, split expenses, track games, and grow your athletic community — public or private.',
            bullets: ['Real-time group chat', 'Expense & payment tracking', 'Game scheduling & calendar', 'Private or public squads'],
            color: '#3b82f6',
            gradient: 'linear-gradient(135deg, rgba(59,130,246,0.12), rgba(99,102,241,0.08))',
            emoji: '💬📅💰🏆',
            flip: true,
          },
          {
            tag: 'Live Scoring',
            icon: '⚡',
            title: 'Score Every Point. Live.',
            desc: 'Built-in scoring for basketball, volleyball, tennis, cricket, ping pong, and more. Spectators follow along in real-time as you play.',
            bullets: ['Basketball, Tennis, Volleyball, Cricket', 'Real-time score updates', 'Set & match tracking', 'Stat recording per player'],
            color: '#22c55e',
            gradient: 'linear-gradient(135deg, rgba(34,197,94,0.12), rgba(16,185,129,0.08))',
            emoji: '📊🎯🏅📱',
            flip: false,
          },
        ].map((f) => (
          <div key={f.tag} className={`grid md:grid-cols-2 gap-12 items-center ${f.flip ? 'md:[&>*:first-child]:order-2' : ''}`}>
            <div>
              <div className="inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4"
                style={{ background: f.color + '18', color: f.color }}>
                {f.tag}
              </div>
              <h2 className="font-black text-3xl md:text-4xl mb-4">{f.title}</h2>
              <p style={{ color: '#9ca3af', lineHeight: 1.7, marginBottom: '1.5rem' }}>{f.desc}</p>
              <ul className="space-y-2 mb-8">
                {f.bullets.map(b => (
                  <li key={b} className="flex items-center gap-3 text-sm" style={{ color: '#d1d5db' }}>
                    <Check size={16} style={{ color: f.color, flexShrink: 0 }} /> {b}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate(isAuthenticated ? '/' : '/signup')}
                className="px-6 py-3 rounded-xl font-bold flex items-center gap-2"
                style={{ background: f.color, color: '#fff' }}>
                Explore {f.tag} <ArrowRight size={16} />
              </button>
            </div>
            <div className="rounded-3xl p-8 flex flex-col gap-4" style={{ background: f.gradient, border: `1px solid ${f.color}20` }}>
              <div className="text-5xl mb-2">{f.icon}</div>
              <div className="grid grid-cols-2 gap-3">
                {f.bullets.map((b, i) => (
                  <div key={b} className="rounded-xl p-4 text-sm font-medium" style={{ background: 'rgba(0,0,0,0.3)', color: '#d1d5db' }}>
                    <div className="text-2xl mb-2">{f.emoji.split('')[i] || '✓'}</div>
                    {b}
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* === PLANS === */}
      <section className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: '#f97316' }}>Pricing</p>
          <h2 className="font-black text-4xl mb-4">Free. Always.</h2>
          <p style={{ color: '#9ca3af', marginBottom: '3rem' }}>Kynaix Sports is completely free for players and organizers. No hidden fees. No credit card required.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {PLANS.map(plan => (
              <div key={plan.name} className="rounded-2xl p-8 relative"
                style={{
                  background: plan.highlight ? `linear-gradient(135deg, ${plan.color}15, rgba(0,0,0,0))` : 'rgba(255,255,255,0.03)',
                  border: `2px solid ${plan.highlight ? plan.color : 'rgba(255,255,255,0.1)'}`,
                  boxShadow: plan.highlight ? `0 0 40px ${plan.color}25` : 'none',
                }}>
                {plan.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-black"
                    style={{ background: plan.color, color: '#fff' }}>{plan.badge}</div>
                )}
                <div className="text-3xl font-black mb-1" style={{ color: plan.color }}>{plan.price}</div>
                <div className="text-xl font-bold mb-6">{plan.name}</div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-3 text-sm" style={{ color: '#d1d5db' }}>
                      <Check size={15} style={{ color: plan.color }} /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/signup')}
                  className="w-full py-3 rounded-xl font-bold transition-all"
                  style={{ background: plan.highlight ? plan.color : 'rgba(255,255,255,0.08)', color: '#fff' }}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === CTA FINAL === */}
      <section className="py-24 px-6 text-center relative overflow-hidden">
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(59,130,246,0.08) 100%)', pointerEvents: 'none' }} />
        <div className="relative max-w-2xl mx-auto">
          <div className="text-6xl mb-6" style={{ animation: 'float 3s ease-in-out infinite' }}>🏆</div>
          <h2 className="font-black mb-4" style={{ fontSize: 'clamp(2rem,5vw,3.5rem)' }}>
            Your next game starts <span style={{ color: '#f97316' }}>now.</span>
          </h2>
          <p style={{ color: '#9ca3af', marginBottom: '2.5rem', fontSize: '1.1rem' }}>
            10,000+ athletes can't be wrong. Join Kynaix Sports and take your game to the next level.
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="px-12 py-5 rounded-2xl font-black text-xl"
            style={{ background: 'linear-gradient(135deg, #f97316, #ef4444)', color: '#fff', boxShadow: '0 8px 60px rgba(249,115,22,0.5)' }}>
            Create Free Account →
          </button>
        </div>
      </section>

      <footer className="py-10 px-6 text-center" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="text-2xl font-black mb-2" style={{ background: 'linear-gradient(90deg,#f97316,#3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Kynaix Sports</div>
        <p style={{ color: '#374151', fontSize: '0.875rem' }}>The Sports Platform Built for Athletes.</p>
      </footer>
    </div>
  );
}
